import { cva } from "class-variance-authority";
import React, { forwardRef } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faTriangleExclamation, faXmark } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Icon, Skeleton, Spinner, Text, Tooltip, cn } from "@performyard/py-components";

export type AttachmentState = "idle" | "uploading" | "error" | "done";

export interface AttachmentProps {
	state: AttachmentState;
	icon: IconDefinition;
	title: string;
	description?: string;
	onRemove?: () => void;
	dangerouslySetClassName?: string;
}

const iconWellVariants = cva("flex h-10 w-10 shrink-0 items-center justify-center rounded-1", {
	variants: {
		state: {
			idle: "bg-primary-1",
			uploading: "bg-neutral-1",
			error: "bg-red-1",
			done: "bg-primary-1",
		},
	},
	defaultVariants: { state: "idle" },
});

const iconColor: Record<AttachmentState, Parameters<typeof Icon>[0]["color"]> = {
	idle: "primary-5",
	uploading: "neutral-5",
	error: "red-5",
	done: "primary-5",
};

const Attachment = forwardRef<HTMLDivElement, AttachmentProps>(
	({ state, icon, title, description, onRemove, dangerouslySetClassName }, ref) => (
		<div
			ref={ref}
			className={cn(
				"flex items-center gap-2 rounded-1 border border-solid border-neutral-3 bg-neutral-0 p-2",
				dangerouslySetClassName
			)}
		>
			<div className={iconWellVariants({ state })}>
				{state === "uploading" ? (
					<Spinner size={24} color="neutral-5" />
				) : (
					<Icon icon={state === "error" ? faTriangleExclamation : icon} color={iconColor[state]} size={24} />
				)}
			</div>
			<div className="flex min-w-0 flex-1 flex-col">
				{state === "uploading" ? (
					<Skeleton className="h-4 w-32" />
				) : (
					<Tooltip content={title}>
						<Text
							size="paragraph-3"
							weight="semi-bold"
							color="neutral-9"
							wrap={false}
							dangerouslySetClassName="truncate text-left"
						>
							{title}
						</Text>
					</Tooltip>
				)}
				{description && (
					<Text size="caption" color="neutral-7">
						{description}
					</Text>
				)}
			</div>
			{onRemove && (
				<ButtonIcon
					icon={faXmark}
					aria-label="Remove"
					variant="ghost"
					color="neutral"
					size={16}
					onClick={onRemove}
				/>
			)}
		</div>
	)
);
Attachment.displayName = "Attachment";

export { Attachment };
